import type { HTMLAttributes } from 'react';
import React from 'react';
import styled, { css } from 'styled-components';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Spacing8, Spacing16, Spacing32 } from 'autogen/design-tokens/tokens';
import type { TypePositionDetailed } from 'types/autogen/contentful';
import ComponentRenderer from '../../shared/ComponentRenderer';

// #region Styles
const getPadding = ($padding: TypePositionDetailed['fields']['padding']) => {
    return () => {
        switch ($padding) {
            case 'None':
                return css`padding: 0;`;
            case 'Small':
                return css`padding: ${Spacing8};`;
            case 'Large':
                return css`
                    padding: ${Spacing32};
                `;
            case 'Medium':
            default:
                return css`
                    padding: ${Spacing16};
                `;
        }
    };
};

const getGap = ($gap: TypePositionDetailed['fields']['gap']) => {
    return () => {
        switch ($gap) {
            case 'None':
                return css`gap: 0;`;
            case 'Small':
                return css`gap: ${Spacing8};`;
            case 'Large':
                return css`
                    gap: ${Spacing32};
                `;
            case 'Medium':
            default:
                return css`
                    gap: ${Spacing16};
                `;
        }
    };
};

const getPositionX = (
    $positionX: TypePositionDetailed['fields']['positionXAxis'],
    $direction: TypePositionDetailed['fields']['direction']
) => {
    return () => {
        if ($direction === 'Vertical') {
            switch ($positionX) {
                case 'Left':
                    return css`align-items: start;`;
                case 'Right':
                    return css`align-items: end;`;
                case 'Center':
                default:
                    return css`align-items: center;`;
            }
        }

        switch ($positionX) {
            case 'Left':
                return css`justify-content: start;`;
            case 'Right':
                return css`justify-content: end;`;
            case 'Center':
            default:
                return css`justify-content: center;`;
        }
    };
};

const getPositionY = (
    $positionY: TypePositionDetailed['fields']['positionYAxis'],
    $direction: TypePositionDetailed['fields']['direction']
) => {
    return () => {
        if ($direction === 'Vertical') {
            switch ($positionY) {
                case 'Bottom':
                    return css`justify-content: end;`;
                case 'Top':
                    return css`justify-content: start;`;
                case 'Center':
                default:
                    return css`justify-content: center;`;
            }
        }

        switch ($positionY) {
            case 'Bottom':
                return css`align-items: end;`;
            case 'Top':
                return css`align-items: start;`;
            case 'Center':
            default:
                return css`align-items: center;`;
        }
    };
};

const PositionWrapper = styled.div<{
    $direction: TypePositionDetailed['fields']['direction'];
    $gap: TypePositionDetailed['fields']['gap'];
    $padding: TypePositionDetailed['fields']['padding'];
    $positionX: TypePositionDetailed['fields']['positionXAxis'];
    $positionY: TypePositionDetailed['fields']['positionYAxis'];
}>`
    display: flex;
    width: 100%;
    height: 100%;
    ${({ $direction }) =>
        $direction === 'Vertical' &&
        css`
            flex-direction: column;
        `}

    ${({ $padding }) => getPadding($padding)}
    ${({ $gap }) => getGap($gap)}
    ${({ $positionX, $direction }) => getPositionX($positionX, $direction)}
    ${({ $positionY, $direction }) => getPositionY($positionY, $direction)}
`;
// #endregion Styles

export default function Position(props: TypePositionDetailed & HTMLAttributes<HTMLDivElement>) {
    const {
        fields,
        metadata,
        sys: { id },
        ...restProps
    } = props;
    const { padding, positionXAxis, positionYAxis, content, direction, gap } = fields;
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    if (!content || content.length <= 0) return null;

    return (
        <PositionWrapper
            $positionX={positionXAxis}
            $positionY={positionYAxis}
            $padding={padding}
            $direction={direction}
            $gap={gap}
            {...inspectorProps({ fieldId: 'internalName' })}
            {...restProps}
        >
            {content.map((item, i) => {
                return (
                    <React.Fragment key={item?.sys.id}>
                        <ComponentRenderer {...item} />
                    </React.Fragment>
                );
            })}
        </PositionWrapper>
    );
}
